import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useEffect, useMemo} from 'react';
import {
  ReactFlow,
  Connection,
  NodeChange,
  NodePositionChange,
  PanOnScrollMode,
  ReactFlowInstance,
  useReactFlow,
  XYPosition,
} from 'reactflow';

import AddSourceStepButton from 'analysis-tool/ui/AddSourceStepButton';
import notifications from 'notifications';
import {ButtonStyle} from 'ui-library/buttons/Button';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import DefaultBackground from 'ui-library/reactflow/backgrounds/DefaultBackground';
import BaseControlButton from 'ui-library/reactflow/controls/BaseControlButton';
import HorizontalControls, {MAX_ZOOM, MIN_ZOOM} from 'ui-library/reactflow/controls/HorizontalControls';
import {useDefaultReactFlowProps} from 'ui-library/reactflow/ReactFlowConstants';
import * as logging from 'util/Logging';
import {confirm} from 'util/Modals';
import removeNulls from 'util/removeNulls';

import {AnalysisFlowchartAction} from './actions/AnalysisFlowchartActionTypes';
import {
  AnalysisGraph,
  AnalysisGraphEdge,
  AnalysisGraphEdgeType,
  AnalysisGraphNodeType,
} from './analysis-graph/AnalysisGraphTypes';
import {useAnalysisFlowchartContext} from './AnalysisFlowchartContext';
import AnalysisFlowchartEdge from './AnalysisFlowchartEdge';
import AnalysisFlowchartNode from './AnalysisFlowchartNode';

const STATIC_PROPS: Partial<Parameters<typeof ReactFlow>[0]> = {
  connectOnClick: false,
  fitView: true,
  fitViewOptions: {padding: 0.2, minZoom: MIN_ZOOM, maxZoom: MAX_ZOOM},
  maxZoom: MAX_ZOOM,
  minZoom: MIN_ZOOM,
  nodesFocusable: false,
  onInit: (reactFlowInstance: ReactFlowInstance) => {
    reactFlowInstance.fitView();
  },
  panOnScroll: true,
  panOnScrollMode: PanOnScrollMode.Free,
  panOnDrag: true,
  selectionKeyCode: null,
  selectNodesOnDrag: false,
};

const EDGE_TYPES = {[AnalysisGraphEdgeType.EDGE]: AnalysisFlowchartEdge};
const NODE_TYPES = {[AnalysisGraphNodeType.STEP]: AnalysisFlowchartNode};

const nodePositionsGlobalState = makeUseGlobalClientState<{[nodeId: string]: XYPosition}>({});

type InternalAnalysisFlowchartProps = AnalysisGraph & {clientStateId: string};

/**
 * Internal helper component for `AnalysisFlowchart`, which assumes `ReactFlowProvider` and
 * `AnalysisFlowchartContext` contexts are available.
 *
 * @package
 */
export default function InternalAnalysisFlowchart({
  nodes,
  edges,
  clientStateId,
}: InternalAnalysisFlowchartProps): JSX.Element {
  const defaultReactFlowProps = useDefaultReactFlowProps();
  const {getActionInfo, highlightedStepIds, clearHighlights} = useAnalysisFlowchartContext();
  const reactFlowInstance = useReactFlow();

  // When highlighted step IDs are provided, zoom and pan to fit only those nodes.
  useEffect(() => {
    if (highlightedStepIds.size > 0 && nodes.length > 0) {
      // Small timeout to allow the initial layout to settle before re-fitting.
      const timer = setTimeout(() => {
        reactFlowInstance.fitView({
          nodes: nodes.filter((node) => highlightedStepIds.has(node.data.step.id)),
          padding: 0.3,
          duration: 300,
          maxZoom: MAX_ZOOM,
        });
      }, 100);
      return () => {
        clearTimeout(timer);
      };
    }
    return undefined;
  }, [highlightedStepIds, nodes, reactFlowInstance]);

  const [nodePositions, setNodePositions] = nodePositionsGlobalState.useGlobalClientState(clientStateId);

  // We store a separate copy of `nodes` internally, to apply more `ReactFlow`-specific properties.
  const updatedNodes = useMemo(
    () =>
      nodes.map((node) => ({
        ...node,
        connectable: getActionInfo(AnalysisFlowchartAction.CONNECT_STEPS, node.data.step.id).enabled,
        position: nodePositions[node.id] ?? node.position,
      })),
    [nodes, getActionInfo, nodePositions]
  );

  // Called by `ReactFlow` when a user takes an action to update a node, such as dragging the node to a new
  // location. See `NodeChange` for the different types of events.
  const onNodesChange = useCallback(
    (changes: Array<NodeChange>) => {
      const newNodePositions = Object.fromEntries(
        changes
          .filter(({type}) => type === 'position')
          .filter(({position}: NodePositionChange) => position != null)
          .map(({id, position}: NodePositionChange) => [id, position!])
      );
      setNodePositions({...nodePositions, ...newNodePositions});
    },
    [nodePositions, setNodePositions]
  );

  // Usually, the layout is refreshed whenever a node is added or removed. If this will be very disruptive,
  // remember nodes' current positions.
  const preventAutoLayout = useCallback(() => {
    setNodePositions(Object.fromEntries(updatedNodes.map((node) => [node.id, node.position])));
  }, [setNodePositions, updatedNodes]);

  // Callback for any new, removed, or updated connections. `edge` is the edge to change or remove (if any),
  // and `connection` is the new edge to create (if any).
  const onConnectionChange = useCallback(
    async (edge: AnalysisGraphEdge | null, connection: Connection | null): Promise<void> => {
      // Grab the IDs of the steps whose inputs will be modified
      const newToStepId = connection && nodes.find((node) => node.id === connection.target)?.data.step.id;
      const oldToStepId = edge && nodes.find((node) => node.id === edge.target)?.data.step.id;
      const {doAction, message} = getActionInfo(
        AnalysisFlowchartAction.CONNECT_STEPS,
        ...removeNulls([newToStepId, oldToStepId])
      );
      if (doAction == null) {
        notifications.info(`This connection cannot be updated. ${message ?? ''}`);
      }
      await doAction?.(edge && edge.data.edge, connection);
    },
    [getActionInfo, nodes]
  );

  // Called by `ReactFlow` when a new edge is drawn
  const onConnect = useCallback(
    (connection: Connection) => onConnectionChange(null, connection),
    [onConnectionChange]
  );

  // Called by `ReactFlow` when an edge is reconnected to a new source or target
  const onEdgeUpdate = useCallback(
    (edge: AnalysisGraphEdge, connection: Connection) => onConnectionChange(edge, connection),
    [onConnectionChange]
  );

  const onEdgesDelete = useCallback(
    (edges: Array<AnalysisGraphEdge>) => {
      // The flowchart UI doesn't currently support deleting multiple edges at once.
      if (edges.length > 1) {
        logging.warn('Tried to delete more than 1 edge');
      }
      if (!getActionInfo(AnalysisFlowchartAction.CONNECT_STEPS, edges[0].data.edge.toStep.id).enabled) {
        return;
      }
      confirm(
        'Delete edge',
        'Deleting this edge will remove the relationship between the steps, possibly causing following steps to fail.',
        ButtonStyle.NEGATIVE,
        {
          enableRedesign: true,
          onSubmit: () => {
            preventAutoLayout(); // Do not auto-layout nodes after an edge is deleted
            onConnectionChange(edges[0], null);
          },
        }
      );
    },
    [onConnectionChange, getActionInfo, preventAutoLayout]
  );

  const nodesConnectable = updatedNodes.some(({connectable}) => connectable);

  return (
    <ReactFlow
      nodes={updatedNodes}
      edges={edges}
      edgeTypes={EDGE_TYPES}
      nodeTypes={NODE_TYPES}
      nodesConnectable={nodesConnectable}
      nodesDraggable={nodesConnectable}
      onMoveStart={highlightedStepIds.size > 0 ? clearHighlights : undefined}
      {...(nodesConnectable ? {onConnect, onEdgeUpdate, onEdgesDelete, onNodesChange} : {})}
      {...defaultReactFlowProps}
      {...STATIC_PROPS}
    >
      <DefaultBackground />
      <div className={css(styles.createNodeButtonPositioningWrapper)}>
        <div className={css(styles.createNodeButton)}>
          <AddSourceStepButton />
        </div>
      </div>
      <HorizontalControls
        additionalControls={
          nodesConnectable ? (
            <BaseControlButton
              icon="magic-wand"
              tooltip="Auto layout"
              onClick={() => {
                setNodePositions({});
              }}
              disabled={Object.values(nodePositions).length === 0} // Disable if auto-layout already in effect
            />
          ) : undefined
        }
      />
    </ReactFlow>
  );
}

const styles = StyleSheet.create({
  createNodeButtonPositioningWrapper: {position: 'absolute', top: '12px', right: '12px'},
  // ReactFlow uses a z-index of 4 for it's flowchart, so to overlay the create button it must be >= 4
  createNodeButton: {position: 'relative', zIndex: 4},
});
